import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Paper,
  IconButton,
  Modal,
  Button,
  TextField,
  Tabs,
  Tab
} from '@mui/material';
import { LocalOffer, Delete, Add, Code } from '@mui/icons-material';
import apiService from '../../services/apiService';
import { useToast } from '../common/ToastProvider';

function CouponCodesCard({ companyId }) {
  const { showSuccess, showError } = useToast();
  const [discountCodes, setDiscountCodes] = useState([]);
  const [featureCodes, setFeatureCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  
  // Delete states
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [codeToDelete, setCodeToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteType, setDeleteType] = useState(''); // 'discount' or 'feature'
  
  // Apply states
  const [applyModalOpen, setApplyModalOpen] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [applyLoading, setApplyLoading] = useState(false);

  useEffect(() => {
    fetchAllCodes();
  }, [companyId]);

  const fetchAllCodes = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch both discount and feature codes in parallel
      const [discountResult, featureResult] = await Promise.all([
        apiService.fetchCompanyDiscountCodeUsages(companyId),
        apiService.fetchCompanyFeatureCodeUsages(companyId)
      ]);
      
      if (discountResult.success) {
        setDiscountCodes(discountResult.data || []);
      } else {
        console.error('Failed to fetch discount codes:', discountResult.error);
      }
      
      if (featureResult.success) {
        setFeatureCodes(featureResult.data || []);
      } else {
        console.error('Failed to fetch feature codes:', featureResult.error);
      }
      
      // Set error if both failed
      if (!discountResult.success && !featureResult.success) {
        setError('Failed to load coupon data');
      }
    } catch (err) {
      setError('Failed to load coupon data');
      console.error('Failed to fetch codes:', err);
    }
    
    setLoading(false);
  };

  const handleDeleteClick = (code, type) => {
    setCodeToDelete(code);
    setDeleteType(type);
    setDeleteModalOpen(true);
  };

  const handleDeleteModalClose = () => {
    setDeleteModalOpen(false);
    setCodeToDelete(null);
    setDeleteType('');
  };

  const handleDeleteConfirm = async () => {
    if (!codeToDelete) return;

    setDeleteLoading(true);

    try {
      let result;
      if (deleteType === 'discount') {
        result = await apiService.deleteDiscountCodeUsage(codeToDelete.unique_id);
      } else {
        result = await apiService.deleteFeatureCodeUsage(codeToDelete.unique_id);
      }

      if (result.success) {
        console.log('Code usage deleted successfully:', result.data);
        // Remove the deleted code from the appropriate list
        if (deleteType === 'discount') {
          setDiscountCodes(prev => prev.filter(code => code.unique_id !== codeToDelete.unique_id));
        } else {
          setFeatureCodes(prev => prev.filter(code => code.unique_id !== codeToDelete.unique_id));
        }
        setDeleteModalOpen(false);
        setCodeToDelete(null);
        setDeleteType('');
      } else {
        console.error('Failed to delete code usage:', result.error);
        showError(`Failed to delete code usage: ${result.error}`, 'Error');
      }
    } catch (error) {
      console.error('Error deleting code usage:', error);
      showError('Failed to delete code usage. Please try again.', 'Error');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleApplyClick = () => {
    setApplyModalOpen(true);
  };

  const handleApplyModalClose = () => {
    setApplyModalOpen(false);
    setCouponCode('');
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;

    setApplyLoading(true);

    try {
      const result = await apiService.applyCouponForCompany(companyId, couponCode.trim());

      if (result.success) {
        console.log('Coupon applied successfully:', result.data);
        // Refresh both lists since we don't know which type was applied
        await fetchAllCodes();
        setCouponCode('');
        setApplyModalOpen(false);
        showSuccess('Coupon applied successfully!', 'Success');
      } else {
        console.error('Failed to apply coupon:', result.error);
        showError(`Failed to apply coupon: ${result.error}`, 'Error');
      }
    } catch (error) {
      console.error('Error applying coupon:', error);
      showError('Failed to apply coupon. Please try again.', 'Error');
    } finally {
      setApplyLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  if (loading) {
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
            🎫 Coupon Codes
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3, flexGrow: 1 }}>
            <CircularProgress size={40} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
            🎫 Coupon Codes
          </Typography>
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
          🎫 Coupon Codes
        </Typography>
        
        <Box sx={{ flexGrow: 1 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={activeTab} onChange={handleTabChange} aria-label="coupon code tabs">
              <Tab 
                label={`Discount Codes (${discountCodes.length})`} 
                icon={<LocalOffer />} 
                iconPosition="start"
              />
              <Tab 
                label={`Feature Codes (${featureCodes.length})`} 
                icon={<Code />} 
                iconPosition="start"
              />
            </Tabs>
          </Box>

        {/* Discount Codes Tab */}
        {activeTab === 0 && (
          <Box>
            {discountCodes.length === 0 ? (
              <Box sx={{ 
                textAlign: 'center', 
                py: 4,
                px: 2
              }}>
                <LocalOffer sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  No discount codes used by this company
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {discountCodes.map((discountCode) => (
                  <Paper 
                    key={discountCode.unique_id} 
                    elevation={1}
                    sx={{ 
                      p: 2.5, 
                      border: '1px solid', 
                      borderColor: 'divider', 
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        borderColor: 'primary.main',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" fontWeight="600" sx={{ fontSize: '1.1rem', mb: 1 }}>
                          {discountCode.discount_code}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(discountCode, 'discount')}
                        sx={{ 
                          color: 'error.main',
                          p: 0.5,
                          '&:hover': {
                            backgroundColor: 'error.light',
                            color: 'white',
                            transform: 'scale(1.1)'
                          },
                          transition: 'all 0.2s ease-in-out'
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                    
                    <Box sx={{ 
                      pt: 2, 
                      borderTop: '1px solid', 
                      borderColor: 'divider' 
                    }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
                        Used On
                      </Typography>
                      <Typography variant="body1" fontWeight="500">
                        {formatDate(discountCode.used_at)}
                      </Typography>
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* Feature Codes Tab */}
        {activeTab === 1 && (
          <Box>
            {featureCodes.length === 0 ? (
              <Box sx={{ 
                textAlign: 'center', 
                py: 4,
                px: 2
              }}>
                <Code sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  No feature codes used by this company
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {featureCodes.map((featureCode) => (
                  <Paper 
                    key={featureCode.unique_id} 
                    elevation={1}
                    sx={{ 
                      p: 2.5, 
                      border: '1px solid', 
                      borderColor: 'divider', 
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        borderColor: 'primary.main',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" fontWeight="600" sx={{ fontSize: '1.1rem', mb: 1 }}>
                          {featureCode.feature_code}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(featureCode, 'feature')}
                        sx={{ 
                          color: 'error.main',
                          p: 0.5,
                          '&:hover': {
                            backgroundColor: 'error.light',
                            color: 'white',
                            transform: 'scale(1.1)'
                          },
                          transition: 'all 0.2s ease-in-out'
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                    
                    <Box sx={{ 
                      pt: 2, 
                      borderTop: '1px solid', 
                      borderColor: 'divider' 
                    }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
                        Used On
                      </Typography>
                      <Typography variant="body1" fontWeight="500">
                        {formatDate(featureCode.used_at)}
                      </Typography>
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}
          </Box>
        )}
        </Box>
      </CardContent>

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteModalOpen}
        onClose={handleDeleteModalClose}
        aria-labelledby="delete-code-modal"
        aria-describedby="delete-code-confirmation"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 450,
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: 4,
        }}>
          <Typography variant="h6" gutterBottom color="error" sx={{ mb: 3, fontWeight: 600 }}>
            Delete {deleteType === 'discount' ? 'Discount' : 'Feature'} Code Usage
          </Typography>
          
          {codeToDelete && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
                Are you sure you want to delete the {deleteType} code usage for:
              </Typography>
              <Box sx={{ 
                p: 3, 
                bgcolor: 'error.light', 
                borderRadius: 2, 
                border: '1px solid',
                borderColor: 'error.main',
                mb: 3
              }}>
                <Typography variant="h6" fontWeight="600" sx={{ mb: 1 }}>
                  {deleteType === 'discount' ? codeToDelete.discount_code : codeToDelete.feature_code}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Used On: {formatDate(codeToDelete.used_at)}
                </Typography>
              </Box>
              <Typography variant="body2" color="error" sx={{ mb: 3, fontWeight: 500 }}>
                This action cannot be undone.
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
            <Button 
              variant="outlined" 
              onClick={handleDeleteModalClose}
              disabled={deleteLoading}
              sx={{ px: 3, py: 1 }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              color="error"
              onClick={handleDeleteConfirm}
              disabled={deleteLoading}
              sx={{ px: 3, py: 1, minWidth: 100 }}
            >
              {deleteLoading ? 'Deleting...' : 'Delete'}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Apply Coupon Modal */}
      <Modal
        open={applyModalOpen}
        onClose={handleApplyModalClose}
        aria-labelledby="apply-coupon-modal"
        aria-describedby="apply-coupon-form"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 450,
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: 4,
        }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
            Apply Coupon Code
          </Typography>
          
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
              Enter a coupon code to apply to this company:
            </Typography>
            
            <TextField
              fullWidth
              label="Coupon Code"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value)}
              placeholder="Enter coupon code"
              disabled={applyLoading}
              sx={{ mb: 3 }}
              autoFocus
            />
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              This will apply the coupon code to the company if it's valid and not already applied. 
              The system will automatically detect if it's a discount code or feature code.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
            <Button 
              variant="outlined" 
              onClick={handleApplyModalClose}
              disabled={applyLoading}
              sx={{ px: 3, py: 1 }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handleApplyCoupon}
              disabled={applyLoading || !couponCode.trim()}
              sx={{ px: 3, py: 1, minWidth: 100 }}
            >
              {applyLoading ? 'Applying...' : 'Apply'}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Card>
  );
}

export default CouponCodesCard; 